import { useState, useEffect } from "react";
import api, { formatBRL, formatDate, formatDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, Check, Clock, Ban } from "lucide-react";
import { toast } from "sonner";
import { RevisaoNfModal, revisaoAberta } from "@/components/fiscal/RevisaoNf";
import { CancelarNfModal } from "@/components/fiscal/CancelarNfModal";
import { datasVenda } from "@/components/fiscal/NfLinha";

function RevisaoInfo({ i }) {
  const revs = i.nf_revisoes || [];
  const ultima = revs[revs.length - 1];
  if (!ultima) return null;
  const aberta = revisaoAberta(i, ultima);
  return (
    <div className={`mt-1.5 text-xs rounded px-2 py-1 border ${aberta ? "bg-red-50 text-red-800 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`revisao-info-${i.id}`}>
      <b>{aberta ? "Revisão aguardando o Fiscal:" : "Última revisão (atendida):"}</b> “{ultima.observacao}” · {formatDateTime(ultima.solicitado_em)}
      {revs.length > 1 && <span className="text-slate-400"> · {revs.length} no total</span>}
    </div>
  );
}

function PedidoCancel({ i, onPedir }) {
  if (i.nf_cancelamento_pedido) {
    return (
      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 rounded px-2 py-0.5 text-xs font-bold" title={i.nf_cancelamento_pedido.motivo} data-testid={`cancel-pedido-${i.id}`}>
        <Ban className="w-3 h-3" /> Cancelamento pedido · aguardando Fiscal
      </span>
    );
  }
  return (
    <Button size="sm" variant="outline" onClick={() => onPedir(i)} className="text-red-700 border-red-200 hover:bg-red-50" data-testid={`btn-pedir-cancel-${i.id}`}>
      <Ban className="w-3 h-3 mr-1" /> Pedir cancelamento
    </Button>
  );
}

function Secao({ titulo, icon: Ico, cor, qtd, vazio, children }) {
  return (
    <Card className="p-5 rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <Ico className={`w-5 h-5 ${cor}`} />
        <h2 className="font-display font-bold text-xl text-slate-900">{titulo}</h2>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{qtd}</span>
      </div>
      {qtd === 0 ? <div className="text-slate-400 text-sm py-3">{vazio}</div> : <div className="space-y-2">{children}</div>}
    </Card>
  );
}

function LinhaEmitida({ i, acoes }) {
  return (
    <div className={`border ${i.alterada_apos_emissao ? "border-amber-400 bg-amber-50/40" : "border-slate-200 bg-white"} rounded-lg p-3`} data-testid={`nf-emitida-${i.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            NF {i.nf_numero} · {i.municipio_nome}<span className="text-slate-400 text-xs">/ {i.municipio_uf}</span>
            {i.alterada_apos_emissao && (
              <span className="inline-flex items-center gap-1 bg-amber-500 text-white rounded px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`nf-alterada-${i.id}`}>⚠️ Alterada — revisar</span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            {i.curso_rotulo || i.curso_nome} · <b className="tabular-nums">{formatBRL(i.valor_vendido)}</b> · Emitida em {formatDate(i.nf_data_emissao)}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => acoes.download(i)} data-testid={`btn-baixar-${i.id}`}><Download className="w-3 h-3 mr-1" /> Baixar NF</Button>
        <Button size="sm" variant="outline" onClick={() => acoes.revisar(i)} className="text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800 focus-visible:text-amber-800" data-testid={`btn-revisar-${i.id}`}>✍️ Solicitar revisão</Button>
        <PedidoCancel i={i} onPedir={acoes.pedir} />
      </div>
      <RevisaoInfo i={i} />
    </div>
  );
}

function LinhaPendente({ i, acoes }) {
  return (
    <div className="border border-amber-200 rounded-lg p-3 bg-amber-50" data-testid={`nf-pendente-${i.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900">{i.municipio_nome}<span className="text-slate-400 text-xs ml-1">/ {i.municipio_uf}</span></div>
          <div className="text-xs text-slate-600">{i.curso_rotulo || i.curso_nome} · <b className="tabular-nums">{formatBRL(i.valor_vendido)}</b> · {datasVenda(i).length > 1 ? "Vendas em" : "Venda em"} {datasVenda(i).map(formatDate).join(", ")}</div>
        </div>
        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 rounded px-2 py-0.5 text-xs font-bold"><Clock className="w-3 h-3" /> Pendente com o Fiscal</span>
        <Button size="sm" variant="outline" onClick={() => acoes.revisar(i)} className="text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800 focus-visible:text-amber-800" data-testid={`btn-revisar-pend-${i.id}`}>✍️ Enviar observação</Button>
        <PedidoCancel i={i} onPedir={acoes.pedir} />
      </div>
      {i.nf_substituir?.length > 0 && (
        <div className="mt-1.5 text-xs rounded px-2 py-1 border bg-blue-50 text-blue-800 border-blue-200" data-testid={`substituir-info-${i.id}`}>
          <b>Inscrição geral:</b> a NF nº {i.nf_substituir.map((s) => s.nf_numero).join(", ")} será cancelada pelo Fiscal e substituída por uma nova com todos os inscritos.
        </div>
      )}
      <RevisaoInfo i={i} />
    </div>
  );
}

function LinhaCancelada({ i }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm" data-testid={`nf-cancelada-${i.id}`}>
      <div className="font-semibold text-slate-700">{i.municipio_nome} <span className="text-slate-400 text-xs">/ {i.municipio_uf}</span> · <span className="line-through tabular-nums">{formatBRL(i.valor_vendido)}</span></div>
      <div className="text-xs text-slate-500">{i.curso_rotulo || i.curso_nome}</div>
      <div className="text-xs text-slate-600 mt-1">Motivo: <b>{i.nf_cancel_motivo}</b> · {i.nf_cancelada_por_nome} · {formatDateTime(i.nf_cancelada_em)}</div>
    </div>
  );
}

export default function MinhasNfs() {
  const [inscs, setInscs] = useState([]);
  const [revisarIns, setRevisarIns] = useState(null);
  const [pedirIns, setPedirIns] = useState(null);
  const load = () => api.get("/minhas-nfs").then((r) => setInscs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const download = async (ins) => {
    try {
      const r = await api.get(`/fiscal/inscricoes/${ins.id}/nf/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = ins.nf_arquivo_nome || `NF_${ins.nf_numero || ins.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar"); }
  };

  const acoes = { download, revisar: setRevisarIns, pedir: setPedirIns };
  const st = (i) => i.nf_status || "pendente";
  const pendentes = inscs.filter((i) => st(i) === "pendente");
  const emitidas = inscs.filter((i) => st(i) === "emitida");
  const canceladas = inscs.filter((i) => st(i) === "cancelada");
  const fecharEAtualizar = (fechar) => () => { fechar(null); load(); };

  return (
    <div className="space-y-6" data-testid="minhas-nfs-page">
      <div className="flex items-center gap-2">
        <FileText className="w-6 h-6 text-blue-600" />
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Minhas Notas Fiscais</h1>
      </div>
      <Secao titulo="Emitidas" icon={Check} cor="text-emerald-600" qtd={emitidas.length} vazio="Nenhuma NF emitida ainda.">
        {emitidas.map((i) => <LinhaEmitida key={i.id} i={i} acoes={acoes} />)}
      </Secao>
      <Secao titulo="Aguardando emissão" icon={Clock} cor="text-amber-600" qtd={pendentes.length} vazio="🎉 Todas suas vendas já têm NF emitida.">
        {pendentes.map((i) => <LinhaPendente key={i.id} i={i} acoes={acoes} />)}
      </Secao>
      {canceladas.length > 0 && (
        <Secao titulo="Canceladas" icon={Ban} cor="text-slate-500" qtd={canceladas.length} vazio="">
          {canceladas.map((i) => <LinhaCancelada key={i.id} i={i} />)}
        </Secao>
      )}
      <RevisaoNfModal ins={revisarIns} onClose={() => setRevisarIns(null)} onDone={fecharEAtualizar(setRevisarIns)} />
      <CancelarNfModal ins={pedirIns} modo="pedir" onClose={() => setPedirIns(null)} onDone={fecharEAtualizar(setPedirIns)} />
    </div>
  );
}
